import cn from "classnames";
import { ReactNode } from "react";

import { TextMessage } from "../../aiConversations.types";
import { AssistantAvatar } from "../AssistantAvatar";
import { BubbleMeta } from "../BubbleMeta";

import styles from "./MessageBubble.module.scss";

interface Props {
  message: TextMessage;
  incomingAvatar?: string;
  incomingName?: string;
  onCorrect?: () => void;
}

// Inline markdown the AI emits: **bold**, *italic* / _italic_, and bare URLs.
const INLINE_MD_REGEX = /(\*\*(.+?)\*\*)|(https?:\/\/\S+)|(\*(.+?)\*)|(_(.+?)_)/g;

const renderRichText = (text: string): ReactNode[] => {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  INLINE_MD_REGEX.lastIndex = 0;
  while ((match = INLINE_MD_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      nodes.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      const url = match[3];
      nodes.push(
        <a
          key={key++}
          className={styles.link}
          href={url}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
        >
          {url}
        </a>
      );
    } else if (match[4] !== undefined) {
      nodes.push(<em key={key++}>{match[5]}</em>);
    } else if (match[6] !== undefined) {
      nodes.push(<em key={key++}>{match[7]}</em>);
    }
    lastIndex = INLINE_MD_REGEX.lastIndex;
  }
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }
  return nodes;
};

const IncomingAvatar = ({ name, url }: { name: string; url?: string }) => {
  if (url) {
    return <img className={styles.incomingAvatarImg} src={url} alt={name} />;
  }
  const initials = name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return <div className={styles.incomingAvatar}>{initials}</div>;
};

const MessageBubble = ({
  message,
  incomingAvatar,
  incomingName = "?",
  onCorrect
}: Props) => {
  const isOutgoing = message.direction === "outgoing";

  return (
    <div
      className={cn(styles.row, {
        [styles.rowOutgoing]: isOutgoing,
        [styles.rowIncoming]: !isOutgoing
      })}
    >
      {!isOutgoing && <IncomingAvatar name={incomingName} url={incomingAvatar} />}

      <div
        className={cn(styles.bubbleWrapper, {
          [styles.bubbleWrapperOutgoing]: isOutgoing,
          [styles.bubbleWrapperIncoming]: !isOutgoing
        })}
      >
        <div
          className={cn(styles.bubble, {
            [styles.bubbleIncoming]: !isOutgoing,
            [styles.bubbleAI]: isOutgoing && message.isAI,
            [styles.bubbleAdmin]: isOutgoing && !message.isAI
          })}
        >
          {renderRichText(message.text)}
        </div>
        <BubbleMeta message={message} onCorrect={onCorrect} />
      </div>

      {isOutgoing && <AssistantAvatar letter="A" size={24} />}
    </div>
  );
};

export default MessageBubble;
